// moderationPipeline — klassificering av prompts mot registry + persistering.
//
// Entrypoint för klient + edge function. Stegen:
//   1. Kör `classifyAgainstRegistry(prompt)` — returnerar första träff
//   2. Om meta-bypass matchar → returnera { flagged: false, isMeta: true }
//      (pipelinen är klar, content-regler körs INTE)
//   3. Om violation- eller advisory-regel matchar → returnera
//      { flagged: true, verdict }
//   4. Annars: { flagged: false } (clean)
//
// Persistering via `record_moderation_event` RPC är separat så vi kan
// klassificera utan att skriva (t.ex. i tester).

import { classifyAgainstRegistry, type ModerationRule } from './moderationRuleRegistry';

export interface ModerationVerdict {
  ruleId: string;
  ruleVersion: number;
  severity: 'advisory' | 'violation';
  countsTowardStrikes: boolean;
  humanReadable: string;
  matchedPattern: string;
  correlationId: string;
}

export interface PipelineResult {
  flagged: boolean;
  isMeta: boolean;
  verdict?: ModerationVerdict;
}

/**
 * Klassificera en prompt (ren-funktion, ingen DB-skrivning).
 */
export function classifyPrompt(prompt: string): PipelineResult {
  const result = classifyAgainstRegistry(prompt);

  if (!result.matched || !result.rule) {
    return { flagged: false, isMeta: false };
  }

  // Meta-bypass: träffen är en meta-fråga → content-regler skippas.
  if (result.skipContentCheck) {
    return { flagged: false, isMeta: true };
  }

  // Violation/advisory-träff → flaggning.
  return {
    flagged: true,
    isMeta: false,
    verdict: {
      ruleId: result.rule.ruleId,
      ruleVersion: result.rule.version,
      severity: result.rule.severity,
      countsTowardStrikes: result.rule.severity === 'violation',
      humanReadable: result.rule.humanReadable,
      matchedPattern: result.matchedPattern ?? '',
      correlationId: makeCorrelationId(),
    },
  };
}

/**
 * Generera correlation-id i ERR-liknande format (matchar ErrorBoundary-
 * konventionen). Format: MOD-<base36-timestamp>-<4char-random>.
 */
export function makeCorrelationId(): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `MOD-${ts}-${rand}`;
}

/**
 * Hjälpfunktion: avgör om en prompt är en meta-fråga utan att klassificera
 * mot content-regler. Används när vi behöver det boolean-beslutet
 * isolerat (t.ex. i Batch CCC:s intent-classifier).
 */
export function isMetaQuery(prompt: string): boolean {
  const result = classifyAgainstRegistry(prompt);
  return result.skipContentCheck === true;
}

/**
 * Hämta en human-readable label för en rule. Fallback till ruleId om
 * regeln inte finns i registry.
 */
export function ruleDisplayName(ruleId: string, lang: 'sv' | 'en' = 'sv'): string {
  const parts = ruleId.split('.');
  if (parts.length < 2) return ruleId;
  const family = parts[0];
  const specific = parts.slice(1).join(' ').replace(/_/g, ' ');
  if (lang === 'en') {
    return `${family}: ${specific}`;
  }
  // Svensk översättning av family-prefixet
  const familyLabel: Record<string, string> = {
    safety: 'Säkerhet',
    abuse: 'Missbruk',
    advisory: 'Notering',
    meta: 'Meta',
  };
  return `${familyLabel[family] ?? family}: ${specific}`;
}

/**
 * Re-export av Rule-typen så att komponenter kan type:a props utan att
 * beroende av registry-filen direkt.
 */
export type { ModerationRule };
